// Developer: Ankit Gupta
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  type ReactNode,
} from 'react';
import Keycloak from 'keycloak-js';
import { Navigate, useLocation } from 'react-router-dom';
import { Spin } from 'antd';
import { apiClient, setKeycloakInstance } from '@/api/client';
import { useAccess, type Permission } from './useAccess';
import { currentLanguage, setLanguage } from '@/i18n';
import { cameFromSignIn, languageOfClaim } from './signInLanguage';

// ─── Keycloak instance (singleton) ──────────────────────────────────────────

const keycloak = new Keycloak({
  url: import.meta.env.VITE_KEYCLOAK_URL ?? 'http://localhost:8180',
  realm: import.meta.env.VITE_KEYCLOAK_REALM ?? 'datapilot',
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? 'datapilot-frontend',
});

// ─── Auth context ─────────────────────────────────────────────────────────────

export interface AuthUser {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  roles: string[];
}

export interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  token: string | undefined;
  user: AuthUser | null;
  roles: string[];
  logout: () => void;
  login: () => void;
  /** Keycloak's registration page: the "Create your company" button of the landing page. */
  register: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// ─── Extract user from token ─────────────────────────────────────────────────

function extractUser(kc: Keycloak): AuthUser | null {
  if (!kc.authenticated || !kc.tokenParsed) return null;
  const tp = kc.tokenParsed as Record<string, unknown>;
  const realmRoles =
    (tp['realm_access'] as { roles?: string[] } | undefined)?.roles ?? [];
  const resourceRoles =
    (
      (tp['resource_access'] as Record<string, { roles?: string[] }> | undefined)?.[
        kc.clientId ?? ''
      ]
    )?.roles ?? [];

  return {
    id: (tp['sub'] as string) ?? '',
    username: (tp['preferred_username'] as string) ?? '',
    email: (tp['email'] as string) ?? '',
    firstName: (tp['given_name'] as string) ?? '',
    lastName: (tp['family_name'] as string) ?? '',
    fullName: (tp['name'] as string) ?? '',
    roles: [...new Set([...realmRoles, ...resourceRoles])],
  };
}

// Where Keycloak sends the person back after sign-in or registration. Never the page's hash:
// Keycloak answers in the fragment, and a return address with one of its own is refused with
// its "page not found". The public pages return to the product itself.
function returnTo(): string {
  const { origin, pathname, search } = window.location;
  if (pathname === '/welcome' || pathname === '/terms') return `${origin}/`;
  return `${origin}${pathname}${search}`;
}

// ─── Provider ─────────────────────────────────────────────────────────────────

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [initialized, setInitialized] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const initCalledRef = useRef(false);

  useEffect(() => {
    // Guard against React StrictMode double-mount
    if (initCalledRef.current) return;
    initCalledRef.current = true;

    keycloak
      .init({
        // check-sso, not login-required: an anonymous visitor sees the landing page (/welcome)
        // and chooses to sign in or register; the route guard sends them to Keycloak from
        // anywhere else. The silent check happens in a hidden iframe on the same origin.
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
        checkLoginIframe: false,
        pkceMethod: 'S256',
      })
      .then(async (auth) => {
        setKeycloakInstance(keycloak);
        // Back from the sign-in pages: the language picked there is the one the product opens
        // in, before the first page is drawn. The account is told too, for what is written away
        // from the page (e-mails, Orion's replies).
        const picked = languageOfClaim((keycloak.tokenParsed as Record<string, unknown> | undefined)?.locale);
        if (auth && cameFromSignIn && picked && picked !== currentLanguage()) {
          await setLanguage(picked);
          apiClient.put('/profile/me', { language: picked }).catch(() => undefined);
        }
        setAuthenticated(auth);
        setInitialized(true);

        // Auto-refresh token. Only for a signed-in session: without one there is nothing to
        // refresh, and the failed refresh used to send an anonymous visitor on the landing or
        // terms page to the login form after thirty seconds.
        setInterval(() => {
          if (!keycloak.authenticated) return;
          keycloak.updateToken(60).catch(() => {
            keycloak.login(keycloakLocale());
          });
        }, 30000);
      })
      .catch((err) => {
        console.error('Keycloak init failed:', err);
        setInitialized(true);
      });
  }, []);

  const user = useMemo(() => extractUser(keycloak), [authenticated]);

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: authenticated,
      isLoading: !initialized,
      token: keycloak.token,
      user,
      roles: user?.roles ?? [],
      // Sign out lands on the product's root, which sends an anonymous visitor to the landing
      // page from inside the app; a direct /welcome address can be shadowed by a proxy rule.
      logout: () => keycloak.logout({ redirectUri: `${window.location.origin}/` }),
      // The sign-in pages open in the product's language (Keycloak's ui_locales); a language
      // picked there comes back in the token and is applied above.
      login: () => keycloak.login({ redirectUri: returnTo(), ...keycloakLocale() }),
      register: () => keycloak.register({ redirectUri: returnTo(), ...keycloakLocale() }),
    }),
    [initialized, authenticated, user],
  );

  if (!initialized) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function keycloakLocale(): { locale: string } {
  return { locale: currentLanguage() };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}

// ─── ProtectedRoute ───────────────────────────────────────────────────────────

interface ProtectedRouteProps {
  children: ReactNode;
  /** A Keycloak realm role the person must hold. */
  requiredRole?: string;
  /** A permission from GET /access/me the person must hold (see useAccess). */
  requiredPermission?: Permission;
}

export function ProtectedRoute({ children, requiredRole, requiredPermission }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, roles, login } = useAuth();
  const location = useLocation();
  // Asked for unconditionally (a hook), used only when a permission is required. Before the
  // person is signed in the request fails and `degraded` would answer; the gates below run
  // first, so it never does.
  const access = useAccess();

  if (isLoading || (isAuthenticated && requiredPermission && access.isLoading)) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // the front door is the landing page; a deep link (an invitation, a report) goes to sign-in
    if (location.pathname === '/' || location.pathname === '') {
      return <Navigate to="/welcome" replace />;
    }
    login();
    return null;
  }

  if (requiredRole && !roles.includes(requiredRole)) {
    return <Navigate to="/unauthorized" state={{ from: location, requiredRole }} replace />;
  }
  if (requiredPermission && !access.can(requiredPermission)) {
    return <Navigate to="/unauthorized" state={{ from: location, requiredPermission }} replace />;
  }

  return <>{children}</>;
}
